import { ACTIVE, AUTHORIZING, BANNED, DELETED, READY } from '@app/constants';

/**
 * Фазы окна подключения WhatsApp (issue/11029).
 *
 * `preparing` — интеграцию перезапустили и ждём, когда она встанет в
 * «Подключается». Раньше кода не будет: CRM отдаёт данные авторизации только
 * для этого статуса, а на «Ошибку» отвечает пустотой. Окно принимало эту
 * пустоту за конец входа и код не показывался уже никогда.
 *
 * `finishing` — код отсканирован или сервер перестал отдавать QR, но
 * подтверждения ещё нет. Это отдельная фаза, потому что сразу после скана шлюз
 * штатно перезапускает сокет, и CRM на несколько секунд показывает «Ошибка»:
 * цепочка Authorizing → Error(reconnecting) → Active. Считать первый «Error»
 * окончательным нельзя — окно закрылось бы неудачей на успешном входе.
 */
export enum WhatsAppAuthPhase {
  Preparing = 'preparing',
  Qr = 'qr',
  Finishing = 'finishing',
  Success = 'success',
  Error = 'error',
}

export interface WhatsAppAuthPhaseInput {
  /** Текущая фаза сессии. */
  phase: WhatsAppAuthPhase;
  /** Статус интеграции из GetIntegrationAuthState; undefined — свежего ответа нет. */
  status?: string | null;
  /** Сколько миллисекунд длится подготовка к получению кода. */
  preparingElapsedMs: number;
  /** Ограничение подготовки. */
  preparingTimeoutMs: number;
  /** Сколько миллисекунд длится ожидание подтверждения. */
  finishingElapsedMs: number;
  /** Ограничение ожидания подтверждения. */
  finishingTimeoutMs: number;
}

export interface WhatsAppAuthPhaseResult {
  phase: WhatsAppAuthPhase;
  errorMessage?: string;
}

export const PREPARING_FAILED_MESSAGE =
  'Интеграция не перешла к подключению. Попробуйте ещё раз';

export const FINISHING_FAILED_MESSAGE =
  'Подключение не завершилось. Проверьте телефон и попробуйте ещё раз';

/**
 * Единственное место, где решается судьба сессии. Источник истины — статус
 * интеграции в CRM, а не событие по вебсокету: событие может не дойти, и тогда
 * окно должно завершиться всё равно.
 */
export const resolveWhatsAppAuthPhase = ({
  phase,
  status,
  preparingElapsedMs,
  preparingTimeoutMs,
  finishingElapsedMs,
  finishingTimeoutMs,
}: WhatsAppAuthPhaseInput): WhatsAppAuthPhaseResult => {
  // Успех и терминальные состояния необратимы: повторные ответы их не меняют.
  if (
    phase === WhatsAppAuthPhase.Success ||
    phase === WhatsAppAuthPhase.Error
  ) {
    return { phase };
  }

  if (status === ACTIVE || status === READY) {
    return { phase: WhatsAppAuthPhase.Success };
  }

  if (status === BANNED) {
    return {
      phase: WhatsAppAuthPhase.Error,
      errorMessage:
        'WhatsApp отклонил подключение (код 403). Проверьте аккаунт в приложении и попробуйте ещё раз.',
    };
  }

  if (status === DELETED) {
    return {
      phase: WhatsAppAuthPhase.Error,
      errorMessage: 'Интеграция удалена',
    };
  }

  if (phase === WhatsAppAuthPhase.Preparing) {
    // Единственный выход к коду — «Подключается». Пока статус другой (в том
    // числе «Ошибка», из которой мы и перезапускаемся), просто ждём.
    if (status === AUTHORIZING) {
      return { phase: WhatsAppAuthPhase.Qr };
    }

    if (preparingElapsedMs >= preparingTimeoutMs) {
      return {
        phase: WhatsAppAuthPhase.Error,
        errorMessage: PREPARING_FAILED_MESSAGE,
      };
    }

    return { phase: WhatsAppAuthPhase.Preparing };
  }

  if (phase === WhatsAppAuthPhase.Finishing) {
    // «Ошибка» и «Подключается» внутри окна ожидания — это перезапуск сокета
    // после скана, а не отказ. Отказом считаем только затянувшееся ожидание.
    if (finishingElapsedMs >= finishingTimeoutMs) {
      return {
        phase: WhatsAppAuthPhase.Error,
        errorMessage: FINISHING_FAILED_MESSAGE,
      };
    }

    return { phase: WhatsAppAuthPhase.Finishing };
  }

  // Фаза QR: «Подключается», «Ошибка» (перезапуск сокета) и отсутствие
  // свежего ответа одинаково означают «код ещё актуален, продолжаем».
  return { phase: WhatsAppAuthPhase.Qr };
};
